import { Injectable, inject } from '@angular/core'
import { CacheType } from './cache-type.enum'
import { EMPTY, Observable } from 'rxjs'
import { CacheInMemoryTimestampService } from './cache-in-memory-timestamp.service'
import { CacheSessionStorageTimestampService } from './cache-session-storage-timestamp.service'
import { CacheLocalStorageTimestampService } from './cache-local-storage-timestamp.service'

/**
 * @author yaqub
 */
@Injectable({ providedIn: 'root' })
export class CacheService {
	private cacheInMemoryService = inject(CacheInMemoryTimestampService)
	private cacheSessionStorageService = inject(CacheSessionStorageTimestampService)
	private cacheLocalStorageService = inject(CacheLocalStorageTimestampService)

	// no tage
	getItemNotExpired(key: string, type: CacheType): Observable<any> {
		if (type === CacheType.IN_MEMORY) {
			return this.cacheInMemoryService.getItemNotExpired(key)
		} else if (type === CacheType.SESSION_STORAGE) {
			return this.cacheSessionStorageService.getItemNotExpired(key)
		} else if (type === CacheType.LOCAL_STORAGE) {
			return this.cacheLocalStorageService.getItemNotExpired(key)
		} else {
			return EMPTY
		}
	}

	updateTimestamp(key: string, hours: number, type: CacheType): void {
		if (type === CacheType.IN_MEMORY) {
			this.cacheInMemoryService.updateTimestamp(key, hours)
		} else if (type === CacheType.SESSION_STORAGE) {
			this.cacheSessionStorageService.updateTimestamp(key, hours)
		} else if (type === CacheType.LOCAL_STORAGE) {
			this.cacheLocalStorageService.updateTimestamp(key, hours)
		}
	}

	isItemAndNotExpired(key: string, type: CacheType): boolean {
		if (type === CacheType.IN_MEMORY) {
			return this.cacheInMemoryService.isItemAndNotExpired(key)
		} else if (type === CacheType.SESSION_STORAGE) {
			return this.cacheSessionStorageService.isItemAndNotExpired(key)
		} else if (type === CacheType.LOCAL_STORAGE) {
			return this.cacheLocalStorageService.isItemAndNotExpired(key)
		} else {
			return false
		}
	}

	setItem(key: string, value: any, type: CacheType): void {
		if (type === CacheType.IN_MEMORY) {
			this.cacheInMemoryService.setItem(key, value)
		} else if (type === CacheType.SESSION_STORAGE) {
			this.cacheSessionStorageService.setItem(key, value)
		} else if (type === CacheType.LOCAL_STORAGE) {
			this.cacheLocalStorageService.setItem(key, value)
		}
	}

	setItemTimed(key: string, value: any, hours: number, type: CacheType): void {
		if (type === CacheType.IN_MEMORY) {
			this.cacheInMemoryService.setItemTimed(key, value, hours)
		} else if (type === CacheType.SESSION_STORAGE) {
			this.cacheSessionStorageService.setItemTimed(key, value, hours)
		} else if (type === CacheType.LOCAL_STORAGE) {
			this.cacheLocalStorageService.setItemTimed(key, value, hours)
		}
	}

	getItem<T = unknown>(key: string, type: CacheType): Observable<T> {
		if (type === CacheType.IN_MEMORY) {
			return this.cacheInMemoryService.getItem(key)
		} else if (type === CacheType.SESSION_STORAGE) {
			return this.cacheSessionStorageService.getItem(key)
		} else if (type === CacheType.LOCAL_STORAGE) {
			/**/
			return this.cacheLocalStorageService.getItem(key)
		} else {
			return EMPTY
		}
	}

	isItem(key: any, type: CacheType): boolean {
		// key = 'selectedLanguage';
		if (type === CacheType.IN_MEMORY) {
			return this.cacheInMemoryService.isItem(key)
		} else if (type === CacheType.SESSION_STORAGE) {
			return this.cacheSessionStorageService.isItem(key)
		} else if (type === CacheType.LOCAL_STORAGE) {
			return this.cacheLocalStorageService.isItem(key)
		} else {
			return false
		}
	}

	removeItemValue(key: string, type: CacheType): void {
		if (type === CacheType.IN_MEMORY) {
			this.cacheInMemoryService.removeItemValue(key)
		} else if (type === CacheType.SESSION_STORAGE) {
			this.cacheSessionStorageService.removeItemValue(key)
		} else if (type === CacheType.LOCAL_STORAGE) {
			this.cacheLocalStorageService.removeItemValue(key)
		}
	}

	removeCompletelyItem(key: string, type: CacheType): void {
		if (type === CacheType.IN_MEMORY) {
			this.cacheInMemoryService.removeCompletelyItem(key)
		} else if (type === CacheType.SESSION_STORAGE) {
			this.cacheSessionStorageService.removeCompletelyItem(key)
		} else if (type === CacheType.LOCAL_STORAGE) {
			this.cacheLocalStorageService.removeCompletelyItem(key)
		}
	}

	removeItemValueWithKeyNoTage(key: string, type: CacheType): void {
		if (type === CacheType.IN_MEMORY) {
			this.cacheInMemoryService.removeItemValueWithKeyNoTage(key)
		} else if (type === CacheType.SESSION_STORAGE) {
			this.cacheSessionStorageService.removeItemValueWithKeyNoTage(key)
		} else if (type === CacheType.LOCAL_STORAGE) {
			this.cacheLocalStorageService.removeItemValueWithKeyNoTage(key)
		}
	}

	removeCompletelyItemWithKeyNoTage(key: string, type: CacheType): void {
		if (type === CacheType.IN_MEMORY) {
			this.cacheInMemoryService.removeCompletelyItemWithKeyNoTage(key)
		} else if (type === CacheType.SESSION_STORAGE) {
			this.cacheSessionStorageService.removeCompletelyItemWithKeyNoTage(key)
		} else if (type === CacheType.LOCAL_STORAGE) {
			this.cacheLocalStorageService.removeCompletelyItemWithKeyNoTage(key)
		}
	}

	// with tage 2
	getItemNotExpired2Tage(key: string, tage1: any, tage2: any, type: CacheType): Observable<any> {
		return this.getItemNotExpired(key + JSON.stringify(tage1) + JSON.stringify(tage2), type)
	}

	updateTimestamp2Tage(key: string, tage1: any, tage2: any, hours: number, type: CacheType): void {
		this.updateTimestamp(key + JSON.stringify(tage1) + JSON.stringify(tage2), hours, type)
	}

	isItemAndNotExpired2Tage(key: string, tage1: any, tage2: any, type: CacheType): boolean {
		return this.isItemAndNotExpired(key + JSON.stringify(tage1) + JSON.stringify(tage2), type)
	}

	setItem2Tage(key: string, tage1: any, tage2: any, value: any, type: CacheType): void {
		this.setItem(key + JSON.stringify(tage1) + JSON.stringify(tage2), value, type)
	}

	setItemTimed2Tage(key: string, tage1: any, tage2: any, value: any, hours: number, type: CacheType): void {
		this.setItemTimed(key + JSON.stringify(tage1) + JSON.stringify(tage2), value, hours, type)
	}

	getItem2Tage(key: string, tage1: any, tage2: any, type: CacheType): Observable<any> {
		return this.getItem(key + JSON.stringify(tage1) + JSON.stringify(tage2), type)
	}

	isItem2Tage(key: string, tage1: any, tage2: any, type: CacheType): boolean {
		return this.isItem(key + JSON.stringify(tage1) + JSON.stringify(tage2), type)
	}

	removeItemValue2Tage(key: string, tage1: any, tage2: any, type: CacheType): void {
		this.removeItemValue(key + JSON.stringify(tage1) + JSON.stringify(tage2), type)
	}

	removeCompletelyItem2Tage(key: string, tage1: any, tage2: any, type: CacheType): void {
		this.removeCompletelyItem(key + JSON.stringify(tage1) + JSON.stringify(tage2), type)
	}

	// with tage 1
	getItemNotExpiredTage(key: string, tage: any, type: CacheType): Observable<any> {
		return this.getItemNotExpired(key + JSON.stringify(tage), type)
	}

	updateTimestampTage(key: string, tage: any, hours: number, type: CacheType): void {
		this.updateTimestamp(key + JSON.stringify(tage), hours, type)
	}

	isItemAndNotExpiredTage(key: string, tage: any, type: CacheType): boolean {
		return this.isItemAndNotExpired(key + JSON.stringify(tage), type)
	}

	setItemTage(key: string, tage: any, value: any, type: CacheType): void {
		this.setItem(key + JSON.stringify(tage), value, type)
	}

	setItemTimedTage(key: string, tage: any, value: any, hours: number, type: CacheType): void {
		this.setItemTimed(key + JSON.stringify(tage), value, hours, type)
	}

	getItemTage(key: string, tage: any, type: CacheType): Observable<any> {
		return this.getItem(key + JSON.stringify(tage), type)
	}

	isItemTage(key: string, tage: any, type: CacheType): boolean {
		return this.isItem(key + JSON.stringify(tage), type)
	}

	removeItemValueTage(key: string, tage: any, type: CacheType): void {
		this.removeItemValue(key + JSON.stringify(tage), type)
	}

	removeCompletelyItemTage(key: string, tage: any, type: CacheType): void {
		this.removeCompletelyItem(key + JSON.stringify(tage), type)
	}
}
